import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useAuth } from 'auth'
import logger from 'logger'

import InputLabel from 'components/atoms/InputLabel'

const resetState = (s) => ({
  ...s,
  confirmCode: '',
  password: '',
  passwordConfirm: '',
})

const ConfirmResetPassword = ({
  setView,
  state,
  setState,
  fetching,
  error,
  setError,
  setFetching,
  authErrorMapping,
}) => {
  const theme = useTheme()
  const { t } = useTranslation('auth')
  const { AmplifyAuth } = useAuth()

  const handleInputChange = (event) => {
    setState((s) => ({ ...s, [event.target.name]: event.target.value }))
  }

  const handleConfirmResetPassword = async (event) => {
    try {
      setFetching(true)
      setError(null)
      if (state.password !== state.passwordConfirm) {
        setError('PasswordsNotEqual')
        return
      }
      await AmplifyAuth.forgotPasswordSubmit(
        state.email,
        state.confirmCode,
        state.password
      )

      setState(resetState)

      setView('resetPasswordSuccess')
    } catch (e) {
      if (e.name === 'AuthError') {
        setError(authErrorMapping[e.log] || e.log)
        return
      }
      if (e.code === 'CodeMismatchException') {
        setError(e.code)
        return
      }
      if (e.code === 'ExpiredCodeException') {
        setError(e.code)
        return
      }
      logger.error(e)
      setError(e.code)
    } finally {
      setFetching(false)
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)

  const handleShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm)
  }

  return (
    <Stack
      width="50%"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        maxWidth: 400,
      }}
    >
      <Typography variant="h4" sx={{ pb: 1 }}>
        <strong>{t('headers.confirmResetPassword.title')}</strong>
      </Typography>

      <Typography
        textAlign="center"
        style={{ color: theme.palette.primary.dark }}
        mb={5}
      >
        {t('messageConfirmResetPassword', state)}
      </Typography>
      <Box sx={{ width: 1 }}>
        <InputLabel
          label={t('attributes.confirmCode.label')}
          inputId="confirm-reset-code"
          sx={{ textAlign: 'left' }}
        />
        <TextField
          id="confirm-reset-code"
          size="small"
          fullWidth
          placeholder={t('attributes.confirmCode.placeholder')}
          name="confirmCode"
          value={state.confirmCode}
          onChange={handleInputChange}
          disabled={fetching}
          autoComplete="one-time-code"
          InputProps={{
            'data-testid': 'confirmResetPassword-confirmCode',
          }}
        />
      </Box>
      <Box mb={5} />
      <Box sx={{ width: 1 }}>
        <InputLabel
          label={t('attributes.password.label')}
          inputId="confirm-reset-password"
          sx={{ textAlign: 'left' }}
        />
        <TextField
          id="confirm-reset-password"
          size="small"
          fullWidth
          placeholder={t('attributes.password.placeholder')}
          type={showPassword ? 'text' : 'password'}
          name="password"
          autoComplete="new-password"
          value={state.password}
          onChange={handleInputChange}
          disabled={fetching}
          InputProps={{
            'data-testid': 'confirmResetPassword-password',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPassword}
                  edge="end"
                  data-testid="confirmResetPassword-toggleViewPassword"
                >
                  {showPassword ? (
                    <VisibilityOff data-testid="confirmResetPassword-passwordVisibleOff" />
                  ) : (
                    <Visibility data-testid="confirmResetPassword-passwordVisibleOn" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mb={5} />
      <Box sx={{ width: 1 }}>
        <InputLabel
          label={t('attributes.passwordConfirm.label')}
          inputId="confirm-reset-password-confirm"
          sx={{ textAlign: 'left' }}
        />
        <TextField
          id="confirm-reset-password-confirm"
          size="small"
          fullWidth
          placeholder={t('attributes.passwordConfirm.placeholder')}
          type={showPasswordConfirm ? 'text' : 'password'}
          name="passwordConfirm"
          autoComplete="new-password"
          value={state.passwordConfirm}
          onChange={handleInputChange}
          disabled={fetching}
          InputProps={{
            'data-testid': 'confirmResetPassword-passwordConfirm',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPasswordConfirm}
                  edge="end"
                  data-testid="confirmResetPassword-toggleViewPasswordConfirm"
                >
                  {showPasswordConfirm ? (
                    <VisibilityOff data-testid="confirmResetPassword-passwordConfirmVisibleOff" />
                  ) : (
                    <Visibility data-testid="confirmResetPassword-passwordConfirmVisibleOn" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mb={5} />
      <Button
        color="primary"
        variant="contained"
        disabled={fetching}
        onClick={handleConfirmResetPassword}
        data-testid="confirmResetPassword-button"
      >
        {t('buttons.confirmResetPassword')}
      </Button>
      {error && (
        <Typography
          textAlign="center"
          p={[3]}
          style={{ color: theme.palette.error.light }}
          data-testid="confirmResetPassword-error"
        >
          {t(`errors.${error}`, state)}
        </Typography>
      )}
      <Box mb={5} />
      <Typography textAlign="center">
        <Trans
          t={t}
          i18nKey="messageChangeAccount"
          components={{
            action: (
              <Typography
                variant="span"
                color="primary"
                data-testid="confirmResetPassword-backToSignin"
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 'bold',
                }}
                onClick={() => {
                  setState(resetState)
                  setView('signIn')
                }}
              />
            ),
          }}
        />
      </Typography>
    </Stack>
  )
}

export default ConfirmResetPassword
